import type { ComponentType } from 'react'
import type { TFunction } from 'i18next'
import { getSignatureStatus } from '../AdditionalSales/bones/signatureStatus'
import { deriveSaleTimeline, type SaleStateKey, type TimelineSaleData } from './deriveSaleTimeline'
import type { ProgressStepState, ProgressTone } from './styled-components'

export interface ProgressSaleData extends TimelineSaleData {
  addressId?: string | number | null
  stepTimes?: Record<string, string>
}

export interface ProgressStep {
  key: string
  label: string
  state: ProgressStepState
  time?: string
}

export interface SaleProgress {
  steps: ProgressStep[]
  tone: ProgressTone
  label: string | null
  labelParams?: Record<string, string>
  isWaiting: boolean
  isPandaDoc: boolean
}

export type SaleStatus = 'new' | 'progress' | 'wait' | 'signed'

export interface ProgressContext {
  country: string
}

export interface ProgressStepDefinition<T> {
  key: string
  label: string
  done: (data: T) => boolean
}

export interface WaitActionsProps {
  saleType: string
  data: ProgressSaleData
  t: TFunction
}

export interface ScriptFormula {
  id: number | string
  name?: string
  attributes?: { price?: string | number }
}

export interface SaleProgressDefinition<T extends ProgressSaleData = ProgressSaleData> {
  kind: 'sale' | 'lead'
  steps: Array<ProgressStepDefinition<T>> | ((context: ProgressContext) => Array<ProgressStepDefinition<T>>)
  cascade?: boolean
  status?: (data: T, steps: ProgressStep[]) => SaleStatus
  doneLabel?: string
  waitLabel?: (data: T) => string
  pandaDoc?: boolean
  waitActions?: ComponentType<WaitActionsProps>
  formulaId?: (data: T) => string | number | null | undefined
  panelValues?: (formula: ScriptFormula | undefined, data: T) => Record<string, string>
}

const PULSE = 'additionalSales:pulse.'

const TONE_BY_STATE: Record<SaleStateKey, ProgressTone> = {
  idle: 'new',
  creating: 'progress',
  create_error: 'error',
  created: 'progress',
  sync_error: 'error',
  synced: 'progress',
  awaiting_signature: 'wait',
  signature_error: 'error',
  signed: 'signed',
}

export const defineSaleProgress = <T extends ProgressSaleData>(definition: SaleProgressDefinition<T>) => definition

export const isSaleStarted = (data: ProgressSaleData) =>
  !!data.actionsInProgress || !!data.createdInDatabase || !!data.saleDatabaseId || !!data.zohoContractDetailId

export const isSaleCreated = (data: ProgressSaleData) =>
  !!data.createdInDatabase || !!data.saleDatabaseId || !!data.zohoContractDetailId

export const isSaleSigned = (data: ProgressSaleData) => getSignatureStatus(data).isSigned

export const hasAddress = (data: ProgressSaleData) => Number(data.addressId) > 0

export const pandaDocWaitLabel = (data: ProgressSaleData) => {
  const { codeStatus, isDocumentLoading } = getSignatureStatus(data)

  return `${PULSE}progress.${!isDocumentLoading && !!codeStatus ? 'pandadoc_waiting' : 'pandadoc_preparing'}`
}

export const defaultSaleStatus = (data: ProgressSaleData, steps: ProgressStep[]): SaleStatus => {
  if (steps.length > 0 && steps[steps.length - 1].state === 'done') return 'signed'
  if (isSaleStarted(data)) return 'wait'
  if (steps.some(step => step.state === 'done') || hasAddress(data)) return 'progress'

  return 'new'
}

const withStates = (
  steps: Array<{ key: string; label: string; done: boolean }>,
  times: Record<string, string>,
  cascade: boolean,
): ProgressStep[] => {
  const reached = steps.map((step, index) => step.done || (cascade && steps.slice(index + 1).some(later => later.done)))
  const current = reached.findIndex(done => !done)

  return steps.map(({ key, label }, index) => ({
    key,
    label,
    state: reached[index] ? 'done' : index === current ? 'current' : 'todo',
    time: reached[index] ? times[key] : undefined,
  }))
}

const scriptedProgress = <T extends ProgressSaleData>(
  data: T,
  definition: SaleProgressDefinition<T>,
  context: ProgressContext,
): SaleProgress => {
  const times = data.stepTimes ?? {}
  const stepDefinitions = typeof definition.steps === 'function' ? definition.steps(context) : definition.steps
  const steps = withStates(
    stepDefinitions.map(({ key, label, done }) => ({ key, label, done: done(data) })),
    times,
    definition.cascade ?? false,
  )
  const base = { steps, isPandaDoc: !!definition.pandaDoc }
  const { stateKey } = deriveSaleTimeline(data)

  if (TONE_BY_STATE[stateKey] === 'error') {
    return { ...base, tone: 'error', label: `${PULSE}state.${stateKey}`, isWaiting: false }
  }

  const status = (definition.status ?? defaultSaleStatus)(data, steps)
  const isLead = definition.kind === 'lead'

  if (status === 'signed') {
    const time = steps.length > 0 ? times[steps[steps.length - 1].key] : undefined
    const label = definition.doneLabel ?? `${PULSE}progress.${isLead ? 'lead_created' : 'signed'}`

    return { ...base, tone: 'signed', label, labelParams: time ? { context: 'at', time } : undefined, isWaiting: false }
  }
  if (status === 'wait') {
    const label =
      definition.waitLabel?.(data) ??
      (definition.pandaDoc ? pandaDocWaitLabel(data) : `${PULSE}progress.waiting_sms_code`)

    return { ...base, tone: 'wait', label, isWaiting: true }
  }
  if (status === 'progress') {
    return {
      ...base,
      tone: 'progress',
      label: `${PULSE}progress.${isLead ? 'lead_in_progress' : 'in_progress'}`,
      isWaiting: false,
    }
  }

  return { ...base, tone: 'new', label: null, isWaiting: false }
}

const genericProgress = (data: ProgressSaleData): SaleProgress => {
  const { stateKey, steps } = deriveSaleTimeline(data)
  const times = data.stepTimes ?? {}

  return {
    steps: steps.map(step => ({
      key: step.key,
      label: `${PULSE}timeline.${step.key}`,
      state: step.state === 'error' ? 'current' : step.state,
      time: step.state === 'done' ? times[step.key] : undefined,
    })),
    tone: TONE_BY_STATE[stateKey],
    label: `${PULSE}state.${stateKey}`,
    isWaiting: false,
    isPandaDoc: false,
  }
}

export const saleProgress = <T extends ProgressSaleData>(
  data: T = {} as T,
  definition?: SaleProgressDefinition<T>,
  context: ProgressContext = { country: '' },
): SaleProgress => (definition ? scriptedProgress(data, definition, context) : genericProgress(data))
